import * as Dialog from '@radix-ui/react-dialog';
import React from 'react';

import { OrderStatus } from 'src/types/enums';
import { updateOrderStatusManually } from '../models';
import type { OrderStatusValue } from '../endpoints/shared.types';
import { useOrder } from '../pages/order/orderContext';
import { Button } from '../../../components/button/Button';
import { Formik } from 'components/form/formik';
import { toast } from 'react-hot-toast';
import { getDisplayError } from '../../../utils/get-display-error';
import { SimpleSelectField } from '../../../components/select/SimpleSelectField';
import { ORDER_STATUS_OPTIONS } from '../constants';
import { nullthrows } from '../../../utils/invariant';
import { captureException } from '@sentry/react';

export interface IOrderStatusDialogProps {
  isOpen: boolean;
  setOpen: (newOpen: boolean) => void;
  orderId: string;
  initialStatus: OrderStatusValue;
}

export const OPTIONS = [
  OrderStatus.Created,
  OrderStatus.Prepayment,
  OrderStatus.Placed,
  OrderStatus.Confirmed,
  OrderStatus.Refused,
  OrderStatus.Executed,
].map((v) => {
  return nullthrows(
    ORDER_STATUS_OPTIONS.find((i) => i.key === v),
    `Order status ${v} does not exist`,
  );
});

export const OrderStatusDialog: React.FC<IOrderStatusDialogProps> = (props) => {
  const { isOpen, setOpen, orderId, initialStatus } = props;
  const { refreshData } = useOrder();

  return (
    <Dialog.Root open={isOpen} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-overlay" />
        <Dialog.Content className="dialog-content">
          <Dialog.Title className="heading-one mb-4">Verander order status manueel</Dialog.Title>

          <Formik
            initialValues={{
              status: ORDER_STATUS_OPTIONS.find((s) => s.key === initialStatus) ?? OPTIONS[0],
            }}
            onSubmit={async (values) => {
              try {
                await updateOrderStatusManually(orderId, {
                  newStatus: values.status.key as OrderStatusValue,
                });
                toast.success('Order status aangepast');
                setOpen(false);
                await refreshData();
              } catch (err: any) {
                captureException(err);
                toast.error('Kon order status niet aanpassen: ' + getDisplayError(err));
              }
            }}
          >
            {({ handleSubmit, isSubmitting, submitForm }) => {
              return (
                <form onSubmit={handleSubmit}>
                  <SimpleSelectField labelText="Nieuwe status" items={OPTIONS} name="status" />

                  <div className="flex justify-between">
                    <Button isDisabled={isSubmitting} isLoading={isSubmitting} onTrigger={() => setOpen(false)}>
                      Annuleer
                    </Button>

                    <Button
                      type="submit"
                      color="primary"
                      isDisabled={isSubmitting}
                      isLoading={isSubmitting}
                      onTrigger={submitForm}
                    >
                      Pas status aan
                    </Button>
                  </div>
                </form>
              );
            }}
          </Formik>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
